import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { postData, ImageFileUpload } from "../../../../utility/Utility";
import Swal from "sweetalert2";

export default function AddTestimonial() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    title: "",
    content: "",
    status: "active"
  });
  const [image, setImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImage(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const toggleStatus = () => {
    setFormData(prev => ({
      ...prev,
      status: prev.status === "active" ? "inactive" : "active"
    }));
  };

  const uploadImage = async () => {
    if (!image) return null;

    const imageFormData = new FormData();
    imageFormData.append("file", image);

    try {
      const response = await ImageFileUpload("api/admin/upload?is_upload=s3", imageFormData);
      if (response?.success) {
        return response.url || response.imageUrl || response.filePath;
      } else {
        toast.error(response?.message || "Image upload failed");
        return null;
      }
    } catch (err) {
      toast.error("Network error during image upload");
      return null;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!formData.title.trim()) {
      toast.error("Please enter a title");
      return;
    }

    if (!formData.content.trim() || formData.content.length < 10) {
      toast.error("Content must be at least 10 characters");
      return;
    }

    setLoading(true);

    try {
      // First upload the image if it exists
      let imagePath = null;
      if (image) {
        imagePath = await uploadImage();
        if (!imagePath) {
          setLoading(false);
          return; // Stop if image upload failed
        }
      }

      // Create data object for API
      const testimonialData = {
        title: formData.title,
        content: formData.content,
        status: formData.status
      };

      // Add image path if available
      if (imagePath) {
        testimonialData.image = imagePath;
      }

      // Call the API
      const response = await postData("api/testimonials/create", testimonialData);

      if (response.success) {
        Swal.fire({
          title: "Success!",
          text: "Testimonial added successfully!",
          icon: "success",
          confirmButtonText: "OK",
        }).then(() => {
          navigate("/testimonial-list");
        });
      } else {
        throw new Error(response.message || "Creation failed");
      }
    } catch (error) {
      console.error("Testimonial creation error:", error);
      toast.error(error.message || "Failed to add testimonial");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="content-page">
      <ToastContainer position="top-right" autoClose={5000} />
      <div className="content">
        <div className="container-fluid">
          <div className="py-3 d-flex align-items-sm-center flex-sm-row flex-column justify-content-between">
            <div className="flex-grow-1">
              <div className="page-title-box">
                <h4 className="mb-1">Testimonial Management</h4>
                <ol className="breadcrumb m-0">
                  <li className="breadcrumb-item">
                  </li>
                  <li className="breadcrumb-item active">Add Testimonial</li>
                </ol>
              </div>
            </div>
            <div>
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={() => navigate("/testimonial-list")}
                disabled={loading}
              >
                <i className="fas fa-arrow-left me-1"></i> Back to List
              </button>
            </div>
          </div>

          <div className="row">
            <div className="col-12">
              <div className="card">
                <div className="card-header">
                  <h5 className="card-title mb-0">
                    Add New Testimonial
                  </h5>
                </div>
                <div className="card-body">
                  <form onSubmit={handleSubmit}>
                    {/* Title Field */}
                    <div className="row mb-3">
                      <div className="col-md-3">
                        <label htmlFor="title" className="form-label fw-medium">
                          Title <span className="text-danger">*</span>
                        </label>
                        <small className="text-muted d-block">The testimonial title</small>
                      </div>
                      <div className="col-md-6">
                        <input
                          type="text"
                          id="title"
                          name="title"
                          className="form-control"
                          placeholder="e.g., OPERATIONAL DIRECTOR"
                          value={formData.title}
                          onChange={handleInputChange}
                          required
                          maxLength={100}
                        />
                        <small className="text-muted">Max 100 characters</small>
                      </div>
                    </div>

                    {/* Content Field */}
                    <div className="row mb-3">
                      <div className="col-md-3">
                        <label htmlFor="content" className="form-label fw-medium">
                          Content <span className="text-danger">*</span>
                        </label>
                        <small className="text-muted d-block">The testimonial description</small>
                      </div>
                      <div className="col-md-6">
                        <textarea
                          className="form-control"
                          id="content"
                          name="content"
                          rows={6}
                          placeholder="Enter detailed content..."
                          value={formData.content}
                          onChange={handleInputChange}
                          required
                          minLength={10}
                        />
                        <small className="text-muted">Minimum 10 characters</small>
                      </div>
                    </div>

                    {/* Image Upload Field */}
                    <div className="row mb-3">
                      <div className="col-md-3">
                        <label htmlFor="image" className="form-label fw-medium">
                          Image
                        </label>
                        <small className="text-muted d-block">Upload a testimonial image</small>
                      </div>
                      <div className="col-md-6">
                        <input
                          type="file"
                          className="form-control"
                          id="image"
                          name="image"
                          accept="image/*"
                          onChange={handleImageChange}
                        />
                        <small className="text-muted">Recommended size: 800x600 pixels</small>

                        {imagePreview && (
                          <div className="mt-2">
                            <img
                              src={imagePreview}
                              alt="Preview"
                              className="img-thumbnail"
                              style={{ maxHeight: '200px' }}
                            />
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Status Field */}
                    <div className="row mb-3">
                      <div className="col-md-3">
                        <label className="form-label fw-medium">Status</label>
                        <small className="text-muted d-block">Active/inactive state</small>
                      </div>
                      <div className="col-md-6">
                        <div className="form-check form-switch">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            id="statusSwitch"
                            checked={formData.status === "active"}
                            onChange={toggleStatus}
                            style={{ width: "3em", height: "1.5em" }}
                          />
                          <label className="form-check-label ms-2" htmlFor="statusSwitch">
                            {formData.status === "active" ? (
                              <span className="text-success fw-medium">Active</span>
                            ) : (
                              <span className="text-danger fw-medium">Inactive</span>
                            )}
                          </label>
                        </div>
                      </div>
                    </div>

                    {/* Form Actions */}
                    <div className="row mt-4">
                      <div className="col-md-6 offset-md-3">
                        <div className="d-flex gap-2">
                          <button
                            type="button"
                            className="btn btn-secondary flex-grow-1"
                            onClick={() => navigate("/testimonial-list")}
                            disabled={loading}
                          >
                            Cancel
                          </button>
                          <button
                            type="submit"
                            className="btn btn-primary flex-grow-1"
                            disabled={loading}
                          >
                            {loading ? (
                              <>
                                <span className="spinner-border spinner-border-sm me-2"></span>
                                Processing...
                              </>
                            ) : (
                              <>
                                <i className="mdi mdi-content-save me-1"></i>
                                Save Testimonial
                              </>
                            )}
                          </button>
                        </div>
                      </div>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}